/**
 * Add-to-cart flourish: a vinyl record slides out of the product jacket,
 * spins, and flies into the header cart; the cart button gives a little bump
 * when it lands. Listens for portal:added-to-cart from s-product-form.js and
 * hands back a promise via detail.waitUntil so the drawer opens right after
 * the landing. Decorative only: skipped for reduced motion, never blocks input.
 */
const DURATION_SLIDE = 260;
const DURATION_FLY = 820;

function visibleJacket() {
  const gallery = document.querySelector('[data-product-gallery]');
  if (!gallery) return null;
  const box = gallery.getBoundingClientRect();
  const centerX = box.left + box.width / 2;
  for (const img of gallery.querySelectorAll('img')) {
    const r = img.getBoundingClientRect();
    if (r.width > 40 && r.left <= centerX && r.right >= centerX) return { img, rect: r };
  }
  const first = gallery.querySelector('img');
  return first ? { img: first, rect: first.getBoundingClientRect() } : null;
}

function buildDisc(size, labelSrc) {
  const disc = document.createElement('div');
  disc.setAttribute('aria-hidden', 'true');
  Object.assign(disc.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    zIndex: '60',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
    background:
      'radial-gradient(circle at 32% 28%, rgba(255,255,255,0.22), transparent 38%),' +
      'repeating-radial-gradient(circle, #0d0d0d 0 1.5px, #1c1c1c 1.5px 3px)',
    boxShadow: '0 8px 22px rgba(0,0,0,0.35)',
  });

  const label = document.createElement('div');
  Object.assign(label.style, {
    position: 'absolute',
    inset: '31%',
    borderRadius: '50%',
    backgroundColor: '#e94b35',
    backgroundImage: labelSrc ? `url("${labelSrc}")` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  });

  const hole = document.createElement('div');
  Object.assign(hole.style, {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: '6%',
    height: '6%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    background: '#f5f5f5',
  });

  label.append(hole);
  disc.append(label);
  return disc;
}

async function fly(detail) {
  const target = document.getElementById('header-cart-trigger');
  const jacket = visibleJacket();
  const from = jacket?.rect || detail.submitter?.getBoundingClientRect?.();
  if (!target || !from) return;

  const to = target.getBoundingClientRect();
  const size = Math.max(90, Math.min(from.width * 0.62, 200));
  const disc = buildDisc(size, jacket?.img.currentSrc || jacket?.img.src || '');
  document.body.append(disc);

  const half = size / 2;
  const startX = from.left + from.width / 2 - half;
  const startY = from.top + from.height / 2 - half;
  const outX = startX + Math.min(from.width * 0.42, 160);
  const endX = to.left + to.width / 2 - half;
  const endY = to.top + to.height / 2 - half;
  const arcY = Math.min(startY, endY) - Math.min(120, window.innerHeight * 0.15);
  const midX = outX + (endX - outX) * 0.55;

  try {
    // Slide out of the jacket.
    await disc.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) rotate(0deg) scale(0.96)`, opacity: 0 },
        { transform: `translate(${outX}px, ${startY}px) rotate(90deg) scale(1)`, opacity: 1 },
      ],
      { duration: DURATION_SLIDE, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'forwards' }
    ).finished;

    // Spin and arc into the cart.
    await disc.animate(
      [
        { transform: `translate(${outX}px, ${startY}px) rotate(90deg) scale(1)` },
        { transform: `translate(${midX}px, ${arcY}px) rotate(560deg) scale(0.62)`, offset: 0.55 },
        { transform: `translate(${endX}px, ${endY}px) rotate(1000deg) scale(0.14)`, opacity: 0.9 },
      ],
      { duration: DURATION_FLY, easing: 'cubic-bezier(0.45, 0, 0.55, 1)', fill: 'forwards' }
    ).finished;
  } finally {
    disc.remove();
  }

  target.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.22)' }, { transform: 'scale(1)' }],
    { duration: 320, easing: 'ease-out' }
  );
}

document.addEventListener('portal:added-to-cart', (event) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const detail = event.detail || {};
  const run = fly(detail).catch(() => {});
  detail.waitUntil?.(run);
});
